import {
  Alert, Box, Checkbox, FormControlLabel, Paper, Typography,
} from '@mui/material';
import type { IntakeFormDef, PublicIntakeDraft } from '../../../../types/intake';
import { RequestSummary } from '../components/RequestSummary';

interface Props {
  def: IntakeFormDef;
  draft: PublicIntakeDraft;
  onChecklistChange: (checklist: Record<string, boolean>) => void;
  warnings: string[];
  serverError: string | null;
}

/**
 * Final step — the full review of everything the client entered followed by
 * the workbook's "double-check" checklist. The checklist deliberately lives
 * HERE, at the end: it confirms what was just reviewed, and submit stays
 * disabled until every box is ticked.
 */
export const ReviewStep = ({ def, draft, onChecklistChange, warnings, serverError }: Props) => (
  <Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
      Review your request
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Check everything below — this is exactly what the lab receives. Use Back to make
      corrections.
    </Typography>

    <RequestSummary def={def} draft={draft} />

    {warnings.length > 0 && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        {warnings.map((w) => <Alert key={w} severity="warning" sx={{ py: 0.25 }}>{w}</Alert>)}
      </Box>
    )}

    {/* The workbook's "double-check" checklist — must-tick to submit */}
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
        Before you submit, please confirm
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
        {def.checklistItems.map((item) => (
          <FormControlLabel
            key={item.id}
            sx={{ alignItems: 'flex-start', ml: 0 }}
            control={
              <Checkbox
                checked={Boolean(draft.checklist?.[item.id])}
                onChange={(e) => onChecklistChange({ ...draft.checklist, [item.id]: e.target.checked })}
                sx={{ pt: 0 }}
                size="small"
              />
            }
            label={<Typography variant="body2">{item.text}</Typography>}
          />
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        If any box cannot be checked, please contact the lab before submitting. You can still
        reopen and edit your request after submitting, up until the lab starts processing it.
      </Typography>
    </Paper>

    {serverError && (
      <Alert severity="error" sx={{ whiteSpace: 'pre-line', mt: 2 }}>{serverError}</Alert>
    )}
  </Box>
);
